import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { TableLink } from '@/components/global';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { CollapsibleSection } from '@/components/global/CollapsibleSection';
import { WarehouseTypeBadge, LocationLink } from '@/components/locations';
import { RAGBadge } from '@/components/global/RAGBadge';
import { Building2, Store, MapPin, Plus } from 'lucide-react';
import { ProductIdentity } from '@/components/products';
import { entityLinkClass } from '@/lib/entityLink';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
  parentWarehouseId: string | null;
  city: string | null;
  isActive: boolean;
  childWarehouses?: Warehouse[];
}

interface Venue {
  id: string;
  name: string;
  city: string;
  parentWarehouseId: string | null;
  isActive: boolean;
}

interface StockAlert {
  warehouseId: string;
  warehouseName: string;
  warehouseType: string;
  productId: string;
  productName: string;
  productSku: string;
  productImageUrl: string | null;
  quantityOnHand: number;
  minLevel: number;
  ragStatus: 'green' | 'amber' | 'red';
}

interface DashboardStats {
  totalHubs: number;
  totalSpokes: number;
  totalVenues: number;
  totalMachines: number;
}

export default function LocationsDashboard() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [alerts, setAlerts] = useState<StockAlert[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalHubs: 0,
    totalSpokes: 0,
    totalVenues: 0,
    totalMachines: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [warehouseRes, venuesRes, alertsRes] = await Promise.all([
        apiFetch('/api/v1/warehouses?includeChildren=true'),
        apiFetch('/api/v1/venues?pageSize=500'),
        apiFetch('/api/v1/stock/alerts?limit=10'),
      ]);

      if (warehouseRes.ok) {
        const data = await warehouseRes.json();
        setWarehouses(data.items || []);

        const hubs = (data.items || []).filter((w: Warehouse) => w.type === 'HUB');
        const spokes = (data.items || []).filter((w: Warehouse) => w.type === 'SPOKE');

        setStats((prev) => ({
          ...prev,
          totalHubs: hubs.length,
          totalSpokes: spokes.length,
        }));
      }

      if (venuesRes.ok) {
        const venueData = await venuesRes.json();
        const venueItems = venueData.items || [];
        setVenues(venueItems);
        setStats((prev) => ({ ...prev, totalVenues: venueItems.length }));
      }

      if (alertsRes.ok) {
        const alertData = await alertsRes.json();
        setAlerts(alertData);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hubs = warehouses.filter((w) => w.type === 'HUB');
  const getVenuesForParent = (parentId: string) =>
    venues.filter((v) => v.parentWarehouseId === parentId);
  const standaloneVenues = venues.filter((v) => !v.parentWarehouseId);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Locations</h1>
          <Button onClick={() => navigate('/locations/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Total Hubs"
            value={stats.totalHubs}
            icon={Building2}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total Spokes"
            value={stats.totalSpokes}
            icon={Store}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total Venues"
            value={stats.totalVenues}
            icon={MapPin}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total Machines"
            value={stats.totalMachines}
            icon={MapPin}
            isLoading={loading}
          />
        </MetricGrid>

        <CollapsibleSection title="Hub & Spoke Hierarchy" defaultOpen className="mb-6">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead className="hidden sm:table-cell">Type</TableHead>
                  <TableHead className="hidden md:table-cell">City</TableHead>
                  <TableHead className="hidden lg:table-cell">Children</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : hubs.length === 0 && standaloneVenues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No locations found
                    </TableCell>
                  </TableRow>
                ) : (
                  hubs.map((hub) => {
                    const hubVenues = getVenuesForParent(hub.id);
                    const childCount = (hub.childWarehouses?.length ?? 0) + hubVenues.length;
                    return (
                      <React.Fragment key={hub.id}>
                        <TableRow className="bg-muted/30">
                          <TableCell>
                            <TableLink
                              to={`/locations/hubs/${hub.id}`}
                              className={entityLinkClass('inline-flex items-center gap-2')}
                            >
                              <Building2 className="h-4 w-4 text-blue-600" />
                              {hub.name}
                            </TableLink>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <WarehouseTypeBadge type="HUB" />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">{hub.city || '-'}</TableCell>
                          <TableCell className="hidden lg:table-cell">{childCount}</TableCell>
                          <TableCell>
                            <RAGBadge status={hub.isActive ? 'green' : 'red'} label={hub.isActive ? 'Active' : 'Inactive'} />
                          </TableCell>
                        </TableRow>
                        {hub.childWarehouses?.map((spoke) => {
                          const spokeVenues = getVenuesForParent(spoke.id);
                          return (
                            <React.Fragment key={spoke.id}>
                              <TableRow>
                                <TableCell className="pl-10">
                                  <TableLink
                                    to={`/locations/spokes/${spoke.id}`}
                                    className={entityLinkClass('inline-flex items-center gap-2')}
                                  >
                                    <Store className="h-4 w-4 text-purple-600" />
                                    {spoke.name}
                                  </TableLink>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">
                                  <WarehouseTypeBadge type="SPOKE" />
                                </TableCell>
                                <TableCell className="hidden md:table-cell">{spoke.city || '-'}</TableCell>
                                <TableCell className="hidden lg:table-cell">{spokeVenues.length > 0 ? spokeVenues.length : '-'}</TableCell>
                                <TableCell>
                                  <RAGBadge status={spoke.isActive ? 'green' : 'red'} label={spoke.isActive ? 'Active' : 'Inactive'} />
                                </TableCell>
                              </TableRow>
                              {spokeVenues.map((venue) => (
                                <TableRow key={venue.id}>
                                  <TableCell className="pl-16">
                                    <TableLink
                                      to={`/locations/venues/${venue.id}`}
                                      className={entityLinkClass('inline-flex items-center gap-2')}
                                    >
                                      <MapPin className="h-4 w-4 text-amber-600" />
                                      {venue.name}
                                    </TableLink>
                                  </TableCell>
                                  <TableCell className="hidden sm:table-cell">
                                    <WarehouseTypeBadge type="VENUE" />
                                  </TableCell>
                                  <TableCell className="hidden md:table-cell">{venue.city || '-'}</TableCell>
                                  <TableCell className="hidden lg:table-cell">-</TableCell>
                                  <TableCell>
                                    <RAGBadge status={venue.isActive ? 'green' : 'red'} label={venue.isActive ? 'Active' : 'Inactive'} />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        {hubVenues.map((venue) => (
                          <TableRow key={venue.id}>
                            <TableCell className="pl-16">
                              <TableLink
                                to={`/locations/venues/${venue.id}`}
                                className={entityLinkClass('inline-flex items-center gap-2')}
                              >
                                <MapPin className="h-4 w-4 text-amber-600" />
                                {venue.name}
                              </TableLink>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <WarehouseTypeBadge type="VENUE" />
                            </TableCell>
                            <TableCell className="hidden md:table-cell">{venue.city || '-'}</TableCell>
                            <TableCell className="hidden lg:table-cell">-</TableCell>
                            <TableCell>
                              <RAGBadge status={venue.isActive ? 'green' : 'red'} label={venue.isActive ? 'Active' : 'Inactive'} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })
                )}
                {!loading && standaloneVenues.length > 0 && (
                  <>
                    {standaloneVenues.map((venue) => (
                      <TableRow key={venue.id} className="bg-muted/20">
                        <TableCell>
                          <TableLink
                            to={`/locations/venues/${venue.id}`}
                            className={entityLinkClass('inline-flex items-center gap-2')}
                          >
                            <MapPin className="h-4 w-4 text-amber-600" />
                            {venue.name}
                          </TableLink>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <WarehouseTypeBadge type="VENUE" />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{venue.city || '-'}</TableCell>
                        <TableCell className="hidden lg:table-cell">-</TableCell>
                        <TableCell>
                          <RAGBadge status={venue.isActive ? 'green' : 'red'} label={venue.isActive ? 'Active' : 'Inactive'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </Card>
        </CollapsibleSection>

        {alerts.length > 0 && (
          <CollapsibleSection title="Low Stock Alerts" defaultOpen className="mb-6">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">On Hand</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Min Level</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <LocationLink
                          locationType={alert.warehouseType}
                          locationId={alert.warehouseId}
                          name={alert.warehouseName}
                        />
                      </TableCell>
                      <TableCell>
                        <ProductIdentity
                          productId={alert.productId}
                          name={alert.productName}
                          sku={alert.productSku}
                          imageUrl={alert.productImageUrl}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell className="text-right">{alert.quantityOnHand}</TableCell>
                      <TableCell className="hidden text-right sm:table-cell">{alert.minLevel}</TableCell>
                      <TableCell>
                        <RAGBadge status={alert.ragStatus} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}
